/**
 * LocationMixin - Vue.js mixin for location functionality
 * 
 * Provides reactive location data and methods to Vue components
 */

import LocationService from '@/services/location_service.js';
import { LOCATION_TEXT } from '@/constants/location.js';

export default {
  data() {
    return {
      locationText: LOCATION_TEXT.DEFAULT,
    };
  },

  computed: {
    currentLocationText() {
      return this.locationText;
    },

    currentLocationData() {
      return LocationService.getCurrentLocationData();
    },
  },

  mounted() {
    this.updateLocationText();
    this.setupLocationListeners();
  },

  beforeUnmount() {
    this.removeLocationListeners();
  },

  methods: {
    /**
     * Update the location text from current location data
     */
    updateLocationText() {
      this.locationText = LocationService.getLocationDisplayText();
    },

    /**
     * Set up event listeners for location changes
     */
    setupLocationListeners() {
      // Listen for custom location change events
      document.addEventListener('location:changed', this.handleLocationChange);
      
      // Listen for storage changes (from other tabs)
      window.addEventListener('storage', this.handleStorageChange);
      
      // Listen for URL changes (popstate)
      window.addEventListener('popstate', this.handleLocationChange);
    },

    /**
     * Remove event listeners
     */
    removeLocationListeners() {
      document.removeEventListener('location:changed', this.handleLocationChange);
      window.removeEventListener('storage', this.handleStorageChange);
      window.removeEventListener('popstate', this.handleLocationChange);
    },

    /**
     * Handle location change events
     */
    handleLocationChange(event) {
      this.updateLocationText();
      
      // Call component-specific handler if it exists
      if (this.onLocationChanged && typeof this.onLocationChanged === 'function') {
        this.onLocationChanged(event);
      }
    },

    /**
     * Handle storage change events (from other tabs/windows)
     */
    handleStorageChange(event) {
      // Only respond to changes to our location storage key
      if (event.key === LocationService.CONFIG?.STORAGE_KEY || event.key === 'supergold_selected_location') {
        this.updateLocationText();
      }
    },

    /**
     * Get current location data (reactive)
     */
    getLocationData() {
      return LocationService.getCurrentLocationData();
    },

    /**
     * Save location data and trigger updates
     */
    setLocationData(locationData) {
      LocationService.saveToStorage(locationData);
      LocationService.dispatchLocationChange(locationData);
    },

    /**
     * Clear location data
     */
    clearLocationData() {
      LocationService.clearStorage();
      LocationService.dispatchLocationChange();
    },

    /**
     * Format address for display
     */
    formatAddress(address) {
      return LocationService.truncateAddress(address);
    },
  },
};